// src/components/Sidebar.jsx
import { NavLink, useNavigate, useLocation } from "react-router";
import { useEffect, useState } from "react";
import Cookies from "js-cookie";
import toast from "react-hot-toast";
import oneSignalService from "../services/oneSignalService";
import { appStorage } from "../utils/appStorage";
import { forceAuthLogout } from "../utils/authSession";
import { env } from "../config/env";

const navItemBase =
  "flex w-full flex-col items-center justify-center gap-1 rounded-xl text-sm transition-colors duration-200 max-md:flex-1 max-md:px-1 max-md:py-2 md:min-h-[4.5rem] md:w-full md:px-2 md:py-2.5";
const navItemIdle = "text-gray-600 hover:bg-black/[0.06]";
const navItemActive = "bg-white text-violet-700 shadow-sm hover:bg-white";

const getNavLinkClass = (active) =>
  `${navItemBase} ${active ? navItemActive : navItemIdle}`;

const NavIcon = ({ src, alt }) => (
  <span className="flex h-7 w-6 shrink-0 items-center justify-center">
    <img src={src} alt={alt} className="max-h-6 max-w-6 object-contain" />
  </span>
);

export default function Sidebar () {
  const navigate = useNavigate();
  const location = useLocation();
  const [lastActivePath, setLastActivePath] = useState(() => {
    try {
      return appStorage.getItem("last_active_sidebar_path") || "/chat";
    } catch (e) {
      return "/chat";
    }
  });
  const [hasNewNotifications, setHasNewNotifications] = useState(false);
  const appVersion = env.version;

  // Track the last active sidebar routenp
  useEffect(() => {
    const currentPath = location.pathname;
    const sidebarRoutes = ['/chat', '/contact', '/groupchat', '/creategroup', '/personal-info', '/notifications'];
    
    // Check if current path matches any sidebar route
    const matchedRoute = sidebarRoutes.find(route => currentPath.startsWith(route));
    
    if (matchedRoute) {
      setLastActivePath(matchedRoute);
      try {
        appStorage.setItem("last_active_sidebar_path", matchedRoute);
      } catch (e) {
        // ignore
      }
    }
    // If no match, keep the last active path
  }, [location.pathname]);

  // Track unread notification flag/count from storage (other parts of the app can set these)
  useEffect(() => {
    // Fetch unread notification count from API on mount
    const fetchUnreadCount = async () => {
      try {
        const token = Cookies.get("auth_token");
        if (!token) return;
        const res = await fetch(`${process.env.REACT_APP_API_BASE_URL}/notifications`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        const data = await res.json();
        if (data.status === "1") {
          const list = data.data || [];
          const unreadCount = list.filter(n => !n.read).length;
          const hasNew = unreadCount > 0;
          appStorage.setItem("unread_notification_count", unreadCount.toString());
          appStorage.setItem("has_new_notification", hasNew ? "true" : "false");
          setHasNewNotifications(hasNew);
        } else {
          appStorage.setItem("unread_notification_count", "0");
          appStorage.setItem("has_new_notification", "false");
          setHasNewNotifications(false);
        }
      } catch (err) {
        // fallback to storage if API fails
        const count = Number(appStorage.getItem("unread_notification_count") || 0);
        const flag = appStorage.getItem("has_new_notification");
        setHasNewNotifications(count > 0 || flag === 'true');
      }
    };

    fetchUnreadCount();
    const readUnread = () => {
      const count = Number(appStorage.getItem("unread_notification_count") || 0);
      const flag = appStorage.getItem("has_new_notification");
      setHasNewNotifications(count > 0 || flag === 'true');
    };
    const handler = () => readUnread();
    window.addEventListener('storage', handler);
    return () => window.removeEventListener('storage', handler);
  }, []);

  // Clear notification badge when visiting notifications page
  useEffect(() => {
    if (location.pathname.startsWith('/notifications')) {
      appStorage.setItem('unread_notification_count', '0');
      appStorage.setItem('has_new_notification', 'false');
      setHasNewNotifications(false);
    }
  }, [location.pathname]);

  const isActiveRoute = (path) => {
    // If current path matches this route, it's active
    if (location.pathname.startsWith(path)) {
      return true;
    }
    // Special-case: treat create-group path as part of group section
    if (path === '/groupchat' && location.pathname.startsWith('/creategroup')) {
      return true;
    }
    // If no sidebar route matches current path, highlight the last active one
    const sidebarRoutes = ['/chat', '/contact', '/groupchat', '/creategroup', '/personal-info', '/notifications'];
    const hasMatch = sidebarRoutes.some(route => location.pathname.startsWith(route));
    
    if (!hasMatch && lastActivePath === path) {
      return true;
    }
    
    return false;
  };

  // Clear persisted active-tab/session keys when leaving profile for another app section
  const clearPersistedProfileTabs = () => {
    try {
      appStorage.removeItem("active_section");
      appStorage.removeItem("active_setting_subsection");
      appStorage.removeItem("active_permission_subtab");
      appStorage.removeItem("active_introduction_subtab");
      appStorage.removeItem("active_section_friend");
    } catch (e) {
      // ignore
    }
  };

  const handleSidebarNavigate = (path) => {
    try {
      appStorage.setItem("last_active_sidebar_path", path);
    } catch (e) {
      // ignore
    }
    clearPersistedProfileTabs();
  };

  const handleProfileNavClick = (event) => {
    if (location.pathname.startsWith("/personal-info")) {
      event.preventDefault();
      try {
        appStorage.setItem("last_active_sidebar_path", "/personal-info");
      } catch (e) {
        // ignore
      }
      window.dispatchEvent(new CustomEvent("phonebook:profile-sidebar-focus"));
      return;
    }
    handleSidebarNavigate("/personal-info");
  };

  const handleLogout = () => {
    // Remove OneSignal external user ID
    oneSignalService.removeExternalUserId()
      .catch(() => {});
    
    // Clear auth token and app session
    forceAuthLogout();
    toast.success("Logged out successfully");
    // Navigate to login using React Router
    navigate("/login", { replace: true });
  };

  return (
    <aside className="mobile-bottom-nav z-50 flex shrink-0 flex-col bg-gray-100 max-md:fixed max-md:inset-x-0 max-md:bottom-0 max-md:w-full max-md:border-t max-md:border-gray-200 max-md:p-2 max-md:pb-[max(0.5rem,env(safe-area-inset-bottom))] max-md:transition-transform max-md:duration-200 md:sticky md:top-0 md:h-screen md:w-20 lg:w-28 md:border-r md:border-gray-200 md:border-t-0 md:p-3">
      {/* Brand */}
      <div className="hidden md:flex items-center justify-center  gap-2 p-2">
        <div className="h-12 w-12">
          {/* Simple PB+ mark (optional SVG) */}
          <img src="/logo.svg" alt="logo" />
        </div>
      </div>

      {/* Nav */}
      <nav className="md:mt-3 md:space-y-2 flex flex-row justify-between items-center gap-1 md:flex-col md:justify-start md:gap-0">
        <NavLink
          to="/chat"
          onClick={() => handleSidebarNavigate("/chat")}
          className={getNavLinkClass(isActiveRoute("/chat"))}
        >
          <NavIcon src="../chatbar/chat.svg" alt="chat" />
          <span className="hidden md:block text-xs leading-tight">Chat</span>
        </NavLink>
        <NavLink
          to="/contact"
          onClick={() => handleSidebarNavigate("/contact")}
          className={getNavLinkClass(isActiveRoute("/contact"))}
        >
          <NavIcon src="../chatbar/connect.svg" alt="contact" />
          <span className="hidden md:block text-xs leading-tight">Contact</span>
        </NavLink>
        <NavLink
          to="/groupchat"
          onClick={() => handleSidebarNavigate("/groupchat")}
          className={getNavLinkClass(isActiveRoute("/groupchat"))}
        >
          <NavIcon src="../chatbar/group.svg" alt="group" />
          <span className="hidden md:block text-xs leading-tight">Group</span>
        </NavLink>

        <NavLink
          to="/personal-info"
          onClick={handleProfileNavClick}
          className={getNavLinkClass(isActiveRoute("/personal-info"))}
        >
          <NavIcon src="../chatbar/profile.svg" alt="profile" />
          <span className="hidden md:block text-xs leading-tight">Profile</span>
        </NavLink>
        <NavLink
          to="/notifications"
          onClick={() => handleSidebarNavigate("/notifications")}
          className={getNavLinkClass(isActiveRoute("/notifications"))}
        >
          <span className="relative flex h-7 w-6 shrink-0 items-center justify-center">
            <img
              src="../chatbar/notification.svg"
              alt="notification"
              className="max-h-6 max-w-6 object-contain"
            />
            {hasNewNotifications && (
              <span className="absolute -top-1 -right-1 inline-flex h-2.5 w-2.5 rounded-full bg-[#7756F6] ring-2 ring-white" />
            )}
          </span>
          <span className="hidden md:block text-xs leading-tight">Notification</span>
        </NavLink>
         <NavLink
         to="/"
        onClick={handleLogout}
        className="md:hidden flex w-full flex-col items-center justify-center gap-1 rounded-xl text-sm transition-colors duration-200 max-md:flex-1 max-md:px-1 max-md:py-2 md:min-h-[4.5rem] md:w-full md:px-2 md:py-2.5"
      >
        <img
          src='../chatbar/logout.svg'
          alt="logout"
          className="max-h-6 max-w-6 object-contain"
        />
        <span className="hidden md:block text-sm font-medium">Logout</span>
      </NavLink>
      </nav>

      {/* Spacer — desktop only */}
      <div className="hidden flex-1 md:block" />

      {/* Version */}
      <div className="hidden md:flex justify-center mb-1 px-2 text-center text-[11px] text-gray-500">
        <span>Version {appVersion}</span>
      </div>

      {/* Logout */}
      <button
        type="button"
        onClick={handleLogout}
        className={`${navItemBase} ${navItemIdle} mt-2 hidden cursor-pointer border-none bg-transparent md:flex`}
      >
        <NavIcon src="../chatbar/logout.svg" alt="logout" />
        <span className="hidden md:block text-xs font-medium leading-tight">Logout</span>
      </button>
    </aside>
  );
}